// Reconstructed from production code; original paths and local names were not available.
import { MEDIA_BASE_URL } from "../services/config.js";
import React from "react";
import { api } from "../services/api.js";
import * as jsxRuntime from "react/jsx-runtime";
const ThemeContext = React.createContext(null);
function parseHexColor(l) {
  if (typeof l != "string") return null;
  const i = l.trim().replace(/^#/, "");
  if (!/^[0-9a-f]{3}([0-9a-f]{3})?$/i.test(i)) return null;
  const c =
      i.length === 3
        ? i
            .split("")
            .map((d) => d + d)
            .join("")
        : i,
    s = Number.parseInt(c, 16);
  return {
    r: (s >> 16) & 255,
    g: (s >> 8) & 255,
    b: s & 255,
  };
}
function relativeLuminance({ r: r, g: g, b: b }) {
  const s = (d) => {
    const f = d / 255;
    return f <= 0.03928 ? f / 12.92 : ((f + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * s(r) + 0.7152 * s(g) + 0.0722 * s(b);
}
function contrastRatio(l, i) {
  const c = relativeLuminance(l),
    s = relativeLuminance(i),
    d = Math.max(c, s),
    f = Math.min(c, s);
  return (d + 0.05) / (f + 0.05);
}
function accessibleTextColor(l, i) {
  const c = parseHexColor(l),
    s = parseHexColor(i);
  return !c || !s
    ? l || "#1f2937"
    : contrastRatio(c, s) >= 4.5
      ? l
      : relativeLuminance(s) > 0.45
        ? "#1f2937"
        : "#ffffff";
}
function accessibleAccentColor(l, i) {
  const c = parseHexColor(l),
    s = parseHexColor("#ffffff");
  return !c || !s || contrastRatio(c, s) < 4.5 ? i : l;
}
function ThemeProvider({ children: children }) {
  const [theme, setTheme] = React.useState({
    site_name: "MyShop",
    primary_color: "#0f766e",
    accent_color: "#d97706",
    logo_url: "",
    background_color: "#f6f0e7",
    products_bg_image_url: null,
    product_assurance_items_json: "",
    show_bulk_quote_callout: 1,
    footer_enabled: 1,
    footer_text: "",
    footer_location: "",
    footer_bg_color: "#fdf7ee",
    footer_text_color: "#1f2937",
    footer_font_family: '"Manrope", "Segoe UI", sans-serif',
    footer_font_size: "0.95rem",
    footer_layout: "horizontal",
    footer_mode: "columns",
    footer_sections_json: "[]",
    footer_image_url: null,
    footer_social_facebook: "",
    footer_social_twitter: "",
    footer_social_linkedin: "",
    footer_social_instagram: "",
    logo: "",
    products_bg_image: "",
    footer_image: "",
    footer_sections: [],
  });
  React.useEffect(() => {
    async function s() {
      try {
        const f = (await api.get("/settings")).data,
          m = f.logo_optimized_url || f.logo_url,
          y = m && !m.startsWith("http") ? `${MEDIA_BASE_URL}${m}` : m,
          g =
            f.products_bg_image_url &&
            !f.products_bg_image_url.startsWith("http")
              ? `${MEDIA_BASE_URL}${f.products_bg_image_url}`
              : f.products_bg_image_url,
          p =
            f.footer_image_url && !f.footer_image_url.startsWith("http")
              ? `${MEDIA_BASE_URL}${f.footer_image_url}`
              : f.footer_image_url;
        let b = [];
        if (f.footer_sections_json)
          try {
            b = JSON.parse(f.footer_sections_json);
          } catch {
            b = [];
          }
        setTheme((x) => ({
          ...x,
          ...f,
          logo: y || "",
          products_bg_image: g || "",
          footer_image: p || "",
          footer_sections: b,
        }));
      } catch (d) {
        console.error("Failed to load theme", d);
      }
    }
    s();
  }, []);
  React.useEffect(() => {
    const s = accessibleAccentColor(theme.primary_color, "#075d4a"),
      d = accessibleAccentColor(theme.accent_color, "#8a631e");
    if (
      (theme.primary_color &&
        (document.documentElement.style.setProperty("--accent", s),
        document.documentElement.style.setProperty("--emerald-500", s),
        document.documentElement.style.setProperty("--emerald-600", s),
        document.documentElement.style.setProperty("--emerald-700", s)),
      theme.accent_color &&
        (document.documentElement.style.setProperty("--accent-strong", d),
        document.documentElement.style.setProperty("--gold-400", d),
        document.documentElement.style.setProperty("--gold-500", d),
        document.documentElement.style.setProperty("--gold-600", d)),
      theme.background_color)
    ) {
      document.documentElement.style.setProperty(
        "--bg-body",
        theme.background_color,
      );
      const m = `radial-gradient(circle at top left, ${theme.background_color} 0%, #f7efe5 45%, #efe3d5 100%)`;
      document.documentElement.style.setProperty("--site-bg-gradient", m);
    }
    const f = theme.products_bg_image
      ? `url("${theme.products_bg_image}")`
      : "none";
    if (
      (document.documentElement.style.setProperty("--products-bg-image", f),
      theme.footer_bg_color &&
        document.documentElement.style.setProperty(
          "--footer-bg",
          theme.footer_bg_color,
        ),
      theme.footer_text_color)
    ) {
      const m = accessibleTextColor(
        theme.footer_text_color,
        theme.footer_bg_color,
      );
      document.documentElement.style.setProperty("--footer-text", m);
    }
    theme.footer_font_family &&
      document.documentElement.style.setProperty(
        "--footer-font-family",
        theme.footer_font_family,
      );
    theme.footer_font_size &&
      document.documentElement.style.setProperty(
        "--footer-font-size",
        theme.footer_font_size,
      );
  }, [
    theme.primary_color,
    theme.accent_color,
    theme.background_color,
    theme.products_bg_image,
    theme.footer_bg_color,
    theme.footer_text_color,
    theme.footer_font_family,
    theme.footer_font_size,
  ]);
  return (
    <ThemeContext.Provider
      value={{
        theme: theme,
        setTheme: setTheme,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
}
function useTheme() {
  return React.useContext(ThemeContext);
}
export {
  ThemeContext,
  parseHexColor,
  relativeLuminance,
  contrastRatio,
  accessibleTextColor,
  accessibleAccentColor,
  ThemeProvider,
  useTheme,
};
